/**
 * AddFromCollectionModal — add items to an Item Pool from an existing
 * Collection's membership.
 *
 * INVARIANT (per collections.ts / collectionsStore.ts): Collections carry no
 * governance or prioritization implications. This modal never creates a
 * governance binding between the Collection and the pool — it reads the
 * Collection's current ordered membership and hands the item ids to the
 * caller's `onAdd`, exactly the same entry point ItemPoolSelectionModal's
 * onSelect / ItemPoolEditor's addPriorityToList path already use. This is
 * NOT collectionsStore.promoteToItemPool() — that creates a separate,
 * governance-bound pool and is not used here.
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Search, FolderOpen, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { useCollectionsStore } from '../../../../stores/prioritization/collectionsStore';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import { useItemPoolsStore } from '../../../../stores/prioritization/itemPoolsStore';
import { ReadinessBadge } from './ReadinessBadge';

export interface AddFromCollectionModalProps {
  open: boolean;
  onClose: () => void;
  poolId: string;
  excludeItemIds: string[];
  onAdd: (itemIds: string[], selectionReason?: string) => void;
}

export function AddFromCollectionModal({ open, onClose, poolId, excludeItemIds, onAdd }: AddFromCollectionModalProps) {
  const collections = useCollectionsStore((s) => s.collections);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  const getOrderedItemIdsForCollection = useCollectionsStore((s) => s.getOrderedItemIdsForCollection);
  const getItemById = useItemsStore((s) => s.getItemById);
  const attributes = useAttributesStore((s) => s.attributes);
  const requiredAttributes = useMemo(() => attributes.filter((a) => a.required), [attributes]);
  const pool = useItemPoolsStore((s) => s.itemPools.find((p) => p.id === poolId));

  const [query, setQuery] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionReason, setSelectionReason] = useState('');

  const excludeSet = useMemo(() => new Set(excludeItemIds), [excludeItemIds]);

  // Reset + (re)fetch on open
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedCollectionId(null);
    setSelectedIds(new Set());
    setSelectionReason('');
    if (collections.length === 0) fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // NOTE: these must stay above the `if (!open) return null` early return
  // below so hook call order never changes between renders.
  const filteredCollections = useMemoFilteredCollections(collections, query);

  const memberRows = useMemo(() => {
    if (!selectedCollectionId) return [];
    return getOrderedItemIdsForCollection(selectedCollectionId)
      .map((itemId) => {
        const item = getItemById(itemId);
        if (!item) return null;
        return { item, alreadyInPool: excludeSet.has(itemId) };
      })
      .filter((x): x is { item: NonNullable<ReturnType<typeof getItemById>>; alreadyInPool: boolean } => x !== null);
  }, [selectedCollectionId, getOrderedItemIdsForCollection, getItemById, excludeSet]);

  if (!open) return null;

  const selectedCollection = selectedCollectionId ? collections.find((c) => c.id === selectedCollectionId) : null;

  const addableCount = memberRows.filter((r) => !r.alreadyInPool).length;

  const handleSelectCollection = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
    setSelectedIds(new Set());
  };

  const toggleRowSelection = (itemId: string, alreadyInPool: boolean) => {
    if (alreadyInPool) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(memberRows.filter((r) => !r.alreadyInPool).map((r) => r.item.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const handleConfirm = () => {
    if (selectedIds.size === 0) return;
    onAdd(Array.from(selectedIds), selectionReason.trim() || undefined);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Add items from collection"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[var(--bg-raised)] shadow-2xl shadow-gray-300/40 border border-[var(--rule)]/80 w-[80vw] max-w-[900px] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            {selectedCollectionId && (
              <button
                onClick={() => setSelectedCollectionId(null)}
                className="p-1 -ml-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div className="flex items-center justify-center w-7 h-7 bg-[var(--slate-dim)]">
              <FolderOpen className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">
                {selectedCollection ? selectedCollection.name : 'Add from Collection'}
              </h2>
              <p className="text-xs text-[var(--text-ter)]">
                {selectedCollection
                  ? `Add items to ${pool?.name ? `"${pool.name}"` : 'this pool'}`
                  : 'Choose a collection to add its items to the pool'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {!selectedCollectionId ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="p-4 border-b border-[var(--rule)] flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  type="text"
                  placeholder="Search collections…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)] outline-none text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {filteredCollections.length === 0 ? (
                <div className="text-center py-10 text-[var(--text-ter)]">
                  <FolderOpen className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
                  <p className="text-sm font-medium text-[var(--text-sec)]">
                    {collections.length === 0 ? 'No collections yet' : 'No matching collections'}
                  </p>
                  {collections.length === 0 && (
                    <p className="text-xs text-[var(--text-ter)] mt-1">Create a collection from the Collections page first.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredCollections.map((c) => {
                    const memberCount = getOrderedItemIdsForCollection(c.id).length;
                    return (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCollection(c.id)}
                        className="w-full flex items-center justify-between gap-3 p-3 border-2 border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/30 text-left transition-all"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[var(--text-pri)] truncate">{c.name}</p>
                          {c.description && <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{c.description}</p>}
                        </div>
                        <span className="flex-shrink-0 text-xs font-medium text-[var(--text-ter)] bg-[var(--hover-tint)] rounded-full px-2 py-0.5">
                          {memberCount} item{memberCount !== 1 ? 's' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-2.5 border-b border-[var(--rule)] flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-[var(--text-ter)]">
                {memberRows.length} item{memberRows.length !== 1 ? 's' : ''} in collection
                {addableCount < memberRows.length && ` · ${memberRows.length - addableCount} already in pool`}
                {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAll}
                  disabled={addableCount === 0}
                  className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed"
                >
                  Select all ({addableCount})
                </button>
                <button
                  onClick={clearSelection}
                  disabled={selectedIds.size === 0}
                  className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {memberRows.length === 0 ? (
                <div className="text-center py-10 text-[var(--text-ter)]">
                  <p className="text-sm">This collection is empty.</p>
                </div>
              ) : (
                <div className="divide-y divide-[var(--rule)]">
                  {memberRows.map(({ item, alreadyInPool }) => {
                    const isChecked = selectedIds.has(item.id) || alreadyInPool;
                    return (
                      <label
                        key={item.id}
                        className={`flex items-start gap-3 px-4 py-2.5 transition-colors ${
                          alreadyInPool ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--surface)]/60'
                        } ${isChecked && !alreadyInPool ? 'bg-[var(--slate-dim)]/50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={alreadyInPool}
                          onChange={() => toggleRowSelection(item.id, alreadyInPool)}
                          className="mt-1 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500 flex-shrink-0 disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                            {alreadyInPool ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)]">
                                <CheckCircle2 className="h-3 w-3" />
                                Already in pool
                              </span>
                            ) : (
                              <ReadinessBadge item={item} requiredAttributes={requiredAttributes} />
                            )}
                          </div>
                          {item.description && <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{item.description}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        {selectedCollectionId && (
          <div className="px-6 py-3 border-t border-[var(--rule)] flex-shrink-0 bg-[var(--surface)]/50">
            {selectedIds.size > 0 && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                  Selection Reason <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Why are these items being included in the pool?"
                  value={selectionReason}
                  onChange={(e) => setSelectionReason(e.target.value)}
                  className="w-full px-3 py-1.5 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)] outline-none text-sm bg-[var(--bg-raised)]"
                />
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-3.5 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedIds.size === 0}
                className="px-4 py-1.5 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 transition-all"
              >
                Add {selectedIds.size > 0 ? selectedIds.size : ''} item{selectedIds.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function useMemoFilteredCollections(collections: ReturnType<typeof useCollectionsStore.getState>['collections'], query: string) {
  return useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...collections].sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return sorted;
    return sorted.filter((c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [collections, query]);
}

export default AddFromCollectionModal;
